import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/FeedbackContext";
import { loginSchema, LoginFormData } from "../../schemas/auth.schema";
import AuthLayout from "../../layouts/AuthLayout";

const getSafeRedirectPath = () => {
  const redirectPath = sessionStorage.getItem("redirect_after_login");
  return redirectPath?.startsWith("/admin") ? redirectPath : "/admin/dashboard";
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading } = useAuth();
  const toast = useToast();
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(getSafeRedirectPath(), { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (sessionStorage.getItem("session_expired") === "1") {
      sessionStorage.removeItem("session_expired");
      toast.warning("Your session expired. Please sign in again.", "Session expired");
    }
  }, [toast]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError("");
      setIsSubmitting(true);
      await login(data.email, data.password);
      const redirectPath = getSafeRedirectPath();
      sessionStorage.removeItem("redirect_after_login");
      navigate(redirectPath);
    } catch (err: any) {
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className="mx-auto w-full max-w-4xl">
        <div className="cms-form-shell overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="flex flex-col items-center justify-center bg-slate-50 p-8 text-center md:p-10">
              <div className="mb-6">
                <img 
                  src="/logo.png" 
                  alt="Logo" 
                  className="mx-auto h-28 w-28 object-contain md:h-36 md:w-36"
                />
              </div>
              <h2 className="cms-type-section-title">Welcome Back</h2>
              <p className="cms-type-helper">Sign in to access your dashboard</p>
            </div>

            <div className="cms-form-shell">
              <div className="mb-6">
                <h1 className="cms-type-section-title">Login</h1>
                <p className="cms-type-helper">Please enter your details</p>
              </div>

              {error && (
                <div className="cms-form-error-banner">
                  <p>{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 cms-form-shell">
                <div>
                  <label htmlFor="email" className="cms-type-form-label">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    {...register("email")}
                    className={`cms-form-control ${errors.email ? "error" : ""} ${
                      errors.email ? "border-rose-500" : "border-slate-300"
                    }`}
                    placeholder="Enter your email"
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <p className="cms-type-error mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="cms-type-form-label">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    {...register("password")}
                    className={`cms-form-control ${errors.password ? "error" : ""} ${
                      errors.password ? "border-rose-500" : "border-slate-300"
                    }`}
                    placeholder="Enter your password"
                    disabled={isSubmitting}
                  />
                  {errors.password && (
                    <p className="cms-type-error mt-1">{errors.password.message}</p>
                  )}
                </div>

                <div className="flex justify-end">
                  <Link to="/forgot-password" className="cms-type-helper">
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="cms-btn-primary w-full"
                >
                  {isSubmitting ? "Signing in..." : "Sign in"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Login;


